import {
  Component, ElementRef,
  input,
  NgModule,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatRadioModule} from "@angular/material/radio";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {catchError, Observable, of, Subscription, tap} from "rxjs";
import {MatChipsModule} from "@angular/material/chips";
import {FormsModule, NgModel} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {ParseJsonPipe} from "../../../../shared/pipes/parse-json.pipe";
import {LanguageService} from "../../../../shared/services/language.service";

@Component({
  selector: 'app-statistic-list-admin',
  standalone: true,
  imports: [
    MatRadioModule,
    MatChipsModule,
    TranslateModule,
    FormsModule,
    ParseJsonPipe
  ],
  templateUrl: './statistic-list-admin.component.html',
  styleUrl: './statistic-list-admin.component.css'
})
export class StatisticListAdminComponent implements OnInit, OnDestroy, OnChanges {
  sectionID = input<any>();
  showStatistic = input<any>();
  productId!: number;
  dataList!: any;
  allocationDataList!: any;
  statistic!: any;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  chartOptions = [
    {value: 'line', viewValue: 'Line Chart'},
    {value: 'bar', viewValue: 'Vertical Bar Chart'},
    {value: 'horizontalBar', viewValue: 'Horizontal Bar Chart'},
    {value: 'pie', viewValue: 'Pie Chart'},
    {value: 'scatter', viewValue: 'Scatter Chart'},
    {value: 'donut', viewValue: 'Donut Chart'},
    {value: '3d_scatter_plot', viewValue: '3D Scatter Plot'},
    {value: 'line_bar_combined', viewValue: 'Line and Bar Chart'},
    {value: 'table', viewValue: 'Table Data'}
  ];

  frequencyOptions = [
    {value: 'daily', viewValue: 'Daily'},
    {value: 'weekly', viewValue: 'Weekly'},
    {value: 'monthly', viewValue: 'Monthly'},
    {value: 'yearly', viewValue: 'Yearly'},
    //{value: 'annualized', viewValue: 'Annualized'},
  ];

  statisticOptions = [
    //{value: 'volatility', viewValue: 'Volatility'},
    {value: 'drawdown', viewValue: 'Drawdown'},
    //{value: 'sharpe_ratio', viewValue: 'Sharpe Ratio'},
    {value: 'performance', viewValue: 'Performance'},
    //{value: 'return', viewValue: 'Return'},
    {value: 'percentage_return', viewValue: 'Percentage Return'},
    {value: 'monthly_returns_table', viewValue: 'Percentage Return Table'}
  ];


  constructor(
    private _snackbarService: SnackbarService,
    private _apiService: DjangoApiService,
    public _route: ActivatedRoute,
    private _languageService: LanguageService,
    private _translate: TranslateService,) {
  }

  ngOnInit(): void {
    this.setupLanguageSubscription();
    this.getRouteParams();
  }

  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }

  private getRouteParams(): void {
    this._route.params.subscribe(params => {
      this.productId = this._route.snapshot.parent?.params['productId'];
    });
    this.loadDataList();
    this.loadAllocationList();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['showStatistic']) {
      this.handleShowStatisticChange(changes['showStatistic'].currentValue);
      //this.loadCustomData();
    }
  }

  handleShowStatisticChange(show: boolean) {
    if (show) {
      this.loadStatisticData();
    } else {
      this.deleteStatistic();
    }
  }

  createEmptyStatistic() {
    this.statistic = {
      section: this.sectionID(),
      frequency: 'daily',
      statistics: [{statistic_choice: 'performance'}],
      chart: 'line'
    }
    this.createStatistic();
  }

  loadStatisticData(): void {
    this.subscriptions.add(
      this._apiService.fetchStatisticList(this.sectionID()).subscribe({
        next: (response: any) => {
          if (response && response.length > 0) {
            this.statistic = response[0]
          } else {
            this.createEmptyStatistic();
          }
        },
        error: (error) => {
          console.log(error);
        }
      })
    );
  }

  private loadDataList(): void {
    this.subscriptions.add(
      this._apiService.fetchDataSourceList(undefined, this.productId).subscribe({
        next: (response: any) => {
          this.dataList = response;
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }


  private loadAllocationList(): void {
    this.subscriptions.add(
      this._apiService.fetchAllocationList(this.productId).subscribe({
        next: (response: any) => {
          this.allocationDataList = response;
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  createStatistic(): void {
    this.subscriptions.add(
      this._apiService.createStatistic(this.statistic).subscribe({
        next: () => {
          this.loadStatisticData();
          this._snackbarService.show('Statistic successfully created');
        },
        error: (error) => {
          console.log(error);
        }
      })
    );
  }

  updateStatistic(): void {
    if (this.statistic && this.statistic.id) {
      this.subscriptions.add(
        this._apiService.updateStatistic(this.statistic).subscribe({
          next: (response) => {
            setTimeout(() => {  // Warte einen Moment, um sicherzustellen, dass die Datenbank aktualisiert ist
              this.loadStatisticData();
            }, 500);
            this._snackbarService.show('Statistic successfully updated');
          },
          error: (error) => {
            console.log(error);
          }
        })
      );
    }
  }

  deleteStatistic(): void {
    if (this.statistic && this.statistic.id) {
      this.subscriptions.add(
        this._apiService.deleteStatistic(this.statistic.id).subscribe({
          next: () => {
            this._snackbarService.show('Statistic successfully deleted');
          },
          error: (error) => {
            console.log(error);
          }
        })
      );
    }
  }

  onChartSelectionChange(selectedChart: string): void {
    this.statistic.chart = selectedChart;
    this.createOrUpdate();
  }

  selectionNotAllowed() {
    this._snackbarService.show('Value is required');
  }

  onFrequencySelectionChange(selectedFrequency: string): void {
    if (this.statistic.frequency == '' || this.statistic.frequency != selectedFrequency) {
      this.statistic.frequency = selectedFrequency;
      this.createOrUpdate();
    } else {
      this.selectionNotAllowed();
    }
  }

  isSelectedStatistic(statisticType: any): boolean {
    return this.statistic.statistics.some((stat: any) => stat.statistic_choice === statisticType);
  }

  onStatisticSelectionChange(statisticType: any): void {
    const index = this.statistic.statistics.findIndex((stat: any) => stat.statistic_choice === statisticType);

    if (index === -1) {
      if (this.statistic.statistics.length > 0) {
        this.statistic.statistics = [];
      }

      this.statistic.statistics.push({statistic_choice: statisticType});
    }

    this.createOrUpdate();
  }


  createOrUpdate() {
    if (this.statistic.id) {
      this.updateStatistic();
    } else {
      this.createStatistic();
    }
  }


  toggleSectionID(data: any, type: 'allocation' | 'datasource'): void {
    const index = data.section.indexOf(this.sectionID());
    if (index > -1) {
      data.section.splice(index, 1);
    } else {
      this.clearAllSections()
      data.section.push(this.sectionID());
    }
    if (type === 'allocation') {
      this.bulkUpdateAllocationData();
    } else {
      this.bulkUpdateCustomData();
    }
  }

  selectPriceData(){
    this.clearAllSections();
    this.bulkUpdateCustomData();
    this.bulkUpdateAllocationData();
  }

  clearAllSections(): void {
    this.dataList.forEach((data:any) => {
      const index = data.section.indexOf(this.sectionID());
      if (index > -1) {
        data.section.splice(index, 1);
      }
    });
    this.allocationDataList.forEach((data:any) => {
      const index = data.section.indexOf(this.sectionID());
      if (index > -1) {
        data.section.splice(index, 1);
      }
    });
  }

  isSectionSelected(data: any): boolean {
    return data.section.includes(this.sectionID());
  }

  isPriceDataSelected(): boolean {
    const dataListSelected = this.dataList?.some((data: any) => this.isSectionSelected(data));
    const allocationDataListSelected = this.allocationDataList?.some((data: any) => this.isSectionSelected(data));
    return !(dataListSelected || allocationDataListSelected);
  }


  bulkUpdateCustomData(): void {
    this._apiService.bulkUpdateDataSource(this.dataList).subscribe({
      next: () => {
        this.loadDataList();
      },
      error: (error) => {
        console.error('There was an error updating the sections in bulk', error);
      }
    });
  }

  bulkUpdateAllocationData(): void {
    this._apiService.bulkUpdateAllocation(this.allocationDataList).subscribe({
      next: () => {
        this.loadAllocationList();
      },
      error: (error) => {
        console.error('There was an error updating the sections in bulk', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
